import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Plus, Calendar } from 'lucide-react';

/**
 * S14 — Maintenance Windows for Rightsizing.
 *
 * Per-cluster schedule restricting when auto-apply runs. Scale-ups
 * (resiliency) bypass the window by default.
 *
 * Backend: PATCH /api/v1/clusters/{cid}/rightsizing/maintenance-window
 *   body: { enabled, windows: [{day, start, end}], bypass_for_scale_up }
 */

const DAYS = ['*', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MaintenanceWindowEditor({ clusterId, onSaved }) {
  const [cfg, setCfg] = useState({
    enabled: false,
    windows: [],
    bypass_for_scale_up: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clusterId) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/v1/clusters/${clusterId}/rightsizing/maintenance-window`,
        );
        const w = r.data?.maintenance_window || {};
        setCfg({
          enabled: !!w.enabled,
          windows: w.windows || [],
          bypass_for_scale_up: w.bypass_for_scale_up !== false,
        });
      } catch (err) {
        // 404 is fine — means no config yet.
      } finally {
        setLoading(false);
      }
    })();
  }, [clusterId]);

  const addWindow = () => setCfg({
    ...cfg,
    windows: [...cfg.windows, { day: '*', start: '02:00', end: '04:00' }],
  });

  const removeWindow = (i) => setCfg({
    ...cfg,
    windows: cfg.windows.filter((_, idx) => idx !== i),
  });

  const updateWindow = (i, key, value) => {
    const next = [...cfg.windows];
    next[i] = { ...next[i], [key]: value };
    setCfg({ ...cfg, windows: next });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/v1/clusters/${clusterId}/rightsizing/maintenance-window`,
        cfg,
      );
      if (onSaved) onSaved(cfg);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><CardContent>Loading windows…</CardContent></Card>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Maintenance Windows</CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="mw-enabled" className="text-sm">Enabled</Label>
            <Switch
              id="mw-enabled"
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            />
          </div>
        </div>
        <div className="text-xs text-gray-500">
          Auto-apply runs only within these UTC windows. Scale-ups bypass
          unless toggled off.
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {error}
          </div>
        )}

        {cfg.windows.length === 0 ? (
          <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed rounded">
            No windows configured. Click "Add window" to restrict apply hours.
          </div>
        ) : (
          <div className="space-y-2">
            {cfg.windows.map((w, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center border rounded p-2 bg-white">
                <div className="col-span-3">
                  <Select value={w.day} onValueChange={(v) => updateWindow(i, 'day', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d === '*' ? 'Any day' : d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="time"
                    value={w.start || '02:00'}
                    onChange={(e) => updateWindow(i, 'start', e.target.value)}
                  />
                </div>
                <div className="col-span-1 text-center text-gray-400">→</div>
                <div className="col-span-3">
                  <Input
                    type="time"
                    value={w.end || '04:00'}
                    onChange={(e) => updateWindow(i, 'end', e.target.value)}
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => removeWindow(i)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button variant="outline" size="sm" onClick={addWindow}>
          <Plus className="w-4 h-4 mr-1" /> Add window
        </Button>

        <div className="flex items-center justify-between border-t pt-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={cfg.bypass_for_scale_up}
              onCheckedChange={(v) => setCfg({ ...cfg, bypass_for_scale_up: v })}
            />
            <Label className="text-sm">
              Always allow scale-ups (safety bypass)
            </Label>
          </div>
          <Button onClick={save} disabled={saving}>
            <Calendar className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
